import { ChannelType } from '@aaa-ncnu-ie/mypolicy-api-registrations';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useOrganicRegistrationForm } from '.';
import { useEmailCheckMutation } from '../../../../api/registration/organic/verifyEmail';
import {
  useOtvcEligibilityMutation,
  UserAlreadyExistsError,
} from '../../../../api/registration/otvc/checkOtvcEligibility';
import { useSendCodeMutation } from '../../../../api/registration/otvc/sendCode';
import ControlledTextField from '../../../../components/TextField/ControlledTextField';
import Button from '../../../../components/styledComponents/Button';
import Link, {
  LinkWithNoRouter,
} from '../../../../components/styledComponents/Link';
import colors from '../../../../constants/colors';
import { useDirectAccessAnalytics } from '../../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../../store/SnackbarProvider';
import PasswordStrength from '../../Components/PasswordStrength';
import ClubRedirectDialog from '../../Dialogs/ClubRedirectDialog';
import SplitView from '../../SplitView';
import {
  OrganicRegistrationFormValues,
  organicRegistrationValidationSchema,
} from '../schemas/organicRegistration';

const EmailAndPasswordForm = () => {
  const { formValues, setFormValues, setOtvcValues } =
    useOrganicRegistrationForm();
  const [showClubRedirectDialog, setShowClubRedirectDialog] = useState(false);
  const [loginUrl, setLoginUrl] = useState('');
  const checkIfEmailExists = useEmailCheckMutation();
  const checkOtvcEligibility = useOtvcEligibilityMutation();
  const navigate = useNavigate();
  const { track } = useDirectAccessAnalytics();
  const { closeSnackbar, enqueueSnackbar } = useSnackbarsContext();
  const sendCodeMutation = useSendCodeMutation();

  const {
    control,
    formState: { isValid },
    getValues,
    reset,
    trigger,
    getFieldState,
  } = useForm<OrganicRegistrationFormValues>({
    resolver: yupResolver(organicRegistrationValidationSchema),
    mode: 'onChange',
    defaultValues: {
      email: formValues?.email,
      emailConfirmation: formValues?.emailConfirmation,
    },
  });

  const currentPassword = useWatch({ control, name: 'password' });

  const handleEmailOnlyOtvc = async (email: string) => {
    closeSnackbar();
    try {
      await sendCodeMutation.mutateAsync({
        channelType: ChannelType.EMAIL,
        channelValue: email,
      });

      setOtvcValues((current) => ({
        ...current,
        codeSent: true,
        flow: 'Email',
      }));
      navigate('code/email');
    } catch (err) {
      track('User Messaged', {
        event_type: 'Messaged',
        location: 'Feature Card',
        message_type: 'Error',
        contents: 'We are unable to process your request right now.',
      });

      // the email OTVC code failed to send for some reason so we just send the user through the manual organic registration process
      navigate('policy');
    }
  };

  const handleContinueClick = async () => {
    const values = getValues();

    try {
      const res = await checkOtvcEligibility.mutateAsync({
        emailId: values.email.toLowerCase(),
        lastName: values.lastName!,
      });
      setFormValues((current) => ({
        ...current,
        ...values,
      }));
      setOtvcValues((current) => ({ ...current, ...res }));

      closeSnackbar();

      if (res.emailEligible && res.smsEligible) {
        navigate('code');
      } else if (res.emailEligible) {
        return handleEmailOnlyOtvc(values.email.toLowerCase());
      } else if (res.smsEligible) {
        navigate('code/sms');
      } else {
        track('Registration Started', {
          event_type: 'Link Accessed',
          location: 'Feature Card',
          registration_type: 'Organic',
          email: values.email,
          login_method: 'DA',
        });
        navigate('policy');
      }
    } catch (err) {
      reset();
      const response = (err as AxiosError<UserAlreadyExistsError>)?.response;
      track('Registration Failed', {
        event_type: 'Automated System Process',
        location: 'Feature Card',
        registration_type: 'Organic',
        flow: 'Email',
        reason: response?.data?.errorCode || 'Email check request failed',
        login_method: 'DA',
      });

      if (response?.status === 409) {
        // user already exists
        track('User Messaged', {
          event_type: 'Messaged',
          location: 'Feature Card',
          message_type: 'Error',
          contents: 'You have a MyPolicy account',
        });
        if (response?.data?.additionalInfo?.loginURL) {
          setShowClubRedirectDialog(true);
          setLoginUrl(response?.data?.additionalInfo?.loginURL);
        } else {
          enqueueSnackbar({
            variant: 'error',
            title: 'You have a MyPolicy account',
            key: 'login-error',
            content: 'Sign in with your username and password',
          });
          navigate('/login');
        }
      }
      window.scrollTo(0, 0);
    }
  };

  return (
    <SplitView data-testid="organic-registration-1">
      <Stack
        justifyContent="center"
        direction="column"
        sx={{
          px: { mobile: 2, tablet: 14, desktop: 11 },
          pb: { mobile: 1, tablet: 4 },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 33, tablet: 44 },
            lineHeight: { mobile: '48px', desktop: '56px' },
          }}
        >
          Create a MyPolicy account
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: { mobile: 23, tablet: 28 },
            lineHeight: { mobile: '32px', desktop: '40px' },
            mt: { mobile: 1, tablet: 1, desktop: 2 },
            color: colors.csaaDark,
          }}
        >
          First time here? Create an account to manage your auto or property
          policy online.
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            fontWeight: 400,
            fontSize: { mobile: 14, tablet: 16 },
            lineHeight: '24px',
            letterSpacing: '0.3px',
            mt: 3,
            color: colors.csaaDark,
          }}
        >
          Even if you have an existing AAA club account, you will need a
          MyPolicy account to sign in and view your insurance policy.
        </Typography>
        <Stack direction="column">
          <ControlledTextField
            name="lastName"
            control={control}
            label="Last name"
            validateCustomerNameInput
            inputProps={{
              autoComplete: 'family-name',
              'data-testid': 'organic-last-name-input',
            }}
            sx={{ mt: 2 }}
            InputProps={{
              sx: { background: colors.csaaWhite },
            }}
          />
          <ControlledTextField
            name="email"
            control={control}
            className="csaa-qm-encrypt"
            label="Email"
            preventWhitespace
            handleBlur={(e, onBlur) => {
              const { isTouched } = getFieldState('emailConfirmation');
              if (!isTouched) {
                trigger('emailConfirmation');
              }
              return onBlur(e);
            }}
            handleChange={(e, onChange) => {
              const { isTouched } = getFieldState('emailConfirmation');
              if (isTouched) {
                trigger('emailConfirmation');
              }
              return onChange(e);
            }}
            sx={{ mt: 2 }}
            inputProps={{
              autoComplete: 'email',
              'data-testid': 'organic-email-input',
            }}
            InputProps={{
              sx: { background: colors.csaaWhite },
              required: true,
            }}
          />
          <ControlledTextField
            name="emailConfirmation"
            control={control}
            className="csaa-qm-encrypt"
            label="Confirm email"
            sx={{ mt: 2 }}
            preventWhitespace
            inputProps={{
              autoComplete: 'email',
              'data-testid': 'organic-email-confirmation-input',
            }}
            InputProps={{
              sx: { background: colors.csaaWhite },
              required: true,
            }}
          />
          <ControlledTextField
            name="password"
            control={control}
            className="csaa-qm-block"
            label="Password"
            preventWhitespace
            inputProps={{
              autoComplete: 'new-password',
              'aria-describedby': currentPassword ? 'password-list' : undefined,
              'data-testid': 'organic-password-input',
            }}
            InputProps={{
              sx: { background: colors.csaaWhite },
              required: true,
            }}
            showVisibilityToggle
            sx={{ mt: 2 }}
          />
          {currentPassword && <PasswordStrength password={currentPassword} />}
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: { mobile: 400 },
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              mt: { mobile: 1, tablet: 2 },
              color: colors.csaaMid,
            }}
          >
            By creating an account, I agree to the{' '}
            <LinkWithNoRouter
              href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/terms-of-use.html"
              target="_blank"
              sx={{ fontSize: 'inherit', fontFamily: 'inherit' }}
              onClick={() =>
                track('Terms and Conditions Accessed', {
                  event_type: 'Link Accessed',
                  link: 'Terms and Conditions',
                  location: 'Feature Card',
                })
              }
            >
              Terms and Conditions
            </LinkWithNoRouter>
            .
          </Typography>
          <Stack
            direction={{ mobile: 'column', tablet: 'row' }}
            justifyContent={{ mobile: 'space-between', tablet: 'flex-end' }}
            spacing={{ mobile: 1, tablet: 0 }}
            sx={{ mt: { mobile: 3 } }}
          >
            <Button
              disabled={!isValid}
              onClick={handleContinueClick}
              role="link"
              data-testid="organic-registration-primary-button"
              loading={
                checkIfEmailExists.isPending ||
                checkOtvcEligibility.isPending ||
                sendCodeMutation.isPending
              }
            >
              Continue
            </Button>
          </Stack>
          <Stack
            direction="row"
            justifyContent={{ mobile: 'flex-start', tablet: 'flex-end' }}
            sx={{ mt: { mobile: 3, tablet: 2 } }}
          >
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: '16px',
                fontWeight: { mobile: 400 },
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                color: colors.csaaMid,
              }}
            >
              Already have a MyPolicy account?{' '}
              <Link
                to="/login"
                sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
              >
                Sign in
              </Link>
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <ClubRedirectDialog
        open={showClubRedirectDialog}
        closeDialog={() => {
          setShowClubRedirectDialog(false);
        }}
        loginUrl={loginUrl}
        loginRedirect
      />
    </SplitView>
  );
};

export default EmailAndPasswordForm;
